import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CreditCardIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { createCheckoutSession, formatCurrency } from '../services/PaymentService';
// Shared singleton — public visitors have no session (anon, token RLS applies),
// same as before; avoids an extra GoTrueClient instance.
import { supabase } from '../utils/supabaseClient';
import HowToPay, { companyCanPayOnline } from '../components/Payment/HowToPay';
import { portalBrand, brandButtonStyle } from '../utils/portalBrand';
import { invoiceTaxSummary, taxLineLabel, isInvoiceVoided, documentServiceLocation } from '@tradesmatepro/shared-ui';

/**
 * Pay Invoice Page - Uses Stripe Checkout Sessions (V2 API)
 * Now uses portal_token for secure public access (like PublicQuote)
 */
const PayInvoice = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // #6213: the pay link is usually opened fresh (email, SMS, Copy Link), so navigate(-1) left
  // the app and showed a blank page. Go back only when there is an in-app entry to go back to;
  // otherwise open this link's portal page (same token).
  const goBack = () => {
    if (location.key && location.key !== 'default') navigate(-1);
    else navigate(`/public/quote/${token}`);
  };
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadInvoice(); }, [token]);

  // Check for payment success from Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && invoice) {
      setSessionId(sessionId);
      setSuccess(true);
      setProcessing(false);
    }
  }, [searchParams, invoice]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 [PayInvoice] Loading invoice with token:', token);

      // Use SECURITY DEFINER RPC to bypass RLS on invoices table
      // Direct queries to invoices/work_orders fail for unauthenticated users
      const { data, error: rpcError } = await supabase
        .rpc('get_invoice_by_portal_token', { p_token: token });

      console.log('🔍 [PayInvoice] RPC result:', { data, rpcError });

      if (rpcError) {
        console.error('❌ [PayInvoice] RPC error:', rpcError);
        setError('Invoice not found or link has expired');
        return;
      }

      if (!data || data.error) {
        console.log('❌ [PayInvoice] Invoice not found:', data?.error);
        setError(data?.error || 'Invoice not found or link has expired');
        return;
      }

      // Flatten the nested result into the shape the rest of the component expects
      const invoiceData = {
        ...data.invoice,
        company: data.company,
        customer: data.customer,
        // Present once 20261024_portal_invoice_service_location is applied; absent = no block.
        service_address: data.service_address || null,
        billing_address: data.billing_address || null,
        // Present once 20261025_portal_invoice_payment_instructions is applied (company_settings
        // .payment_instructions, blank = null); absent = the generic "contact the company" line.
        payment_instructions: data.payment_instructions || null,
      };

      console.log('✅ [PayInvoice] Invoice loaded:', invoiceData);

      setInvoice(invoiceData);
    } catch (err) {
      setError(err.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    try {
      setProcessing(true);
      setError(null);

      const amountDue = Number(invoice.balance_due ?? invoice.total_amount ?? invoice.amount ?? 0);
      if (isInvoiceVoided(invoice.status)) {
        throw new Error('This invoice was voided. No payment is due.');
      }
      if (invoice.status === 'paid' || amountDue <= 0) {
        throw new Error('This invoice has already been paid.');
      }

      const result = await createCheckoutSession({
        amount: amountDue,
        companyId: invoice.company_id,
        customerId: invoice.customer_id,
        invoiceId: invoice.id,
        jobId: invoice.work_order_id || invoice.job_id,
        workOrderId: invoice.work_order_id || invoice.job_id,
        jobTitle: `Invoice #${invoice.invoice_number}`,
        description: `Payment for Invoice #${invoice.invoice_number}`,
        customerEmail: invoice.customer?.email,
      });

      if (result.data?.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err) {
      setError(err.message || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationCircleIcon className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Invoice</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (success || sessionId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your payment. A receipt has been sent to your email.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Invoice #</span>
              <span className="font-medium">{invoice?.invoice_number}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-500">Amount</span>
              <span className="font-medium">{formatCurrency(invoice?.total_amount || invoice?.amount)}</span>
            </div>
            {sessionId && (
              <div className="flex justify-between text-sm mt-2">
                <span className="text-gray-500">Transaction ID</span>
                <span className="font-medium text-xs">{sessionId.slice(0, 20)}...</span>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const amountDue = Number(invoice?.balance_due ?? invoice?.total_amount ?? invoice?.amount ?? 0);
  const serviceLocation = invoice?.service_address
    ? documentServiceLocation(invoice.service_address, invoice.billing_address)
    : null;
  const invoicePaid = invoice?.status === 'paid' || amountDue <= 0;
  // Operator ruling 2026-10-25 A (#6211): a company that cannot take online payments (Stripe
  // onboarding not complete — the flag stripe-connect-status keeps in sync with Stripe) shows the
  // invoice with no Pay button and no error, and its own payment instructions instead. This page
  // used to set an error AND render the live Pay button for these companies.
  const canPayOnline = companyCanPayOnline(invoice?.company);

  // The breakdown above Amount Due (operator 2026-10-21: anywhere money is shown, show taxes).
  // Only the SAVED figures from get_invoice_by_portal_token (20261021_portal_invoice_tax_detail);
  // the tax row's label comes from the shared documentTax owner. Before that migration the RPC
  // has no subtotal/tax keys and the page shows Amount Due alone, as it always did.
  const hasBreakdown = invoice?.subtotal != null && invoice?.tax_amount != null;
  const savedSubtotal = Number(invoice?.subtotal || 0);
  const savedTax = Number(invoice?.tax_amount || 0);
  const savedTotal = Number(invoice?.total_amount || 0);
  const amountPaid = Number(invoice?.amount_paid || 0);
  // Some legacy invoices store subtotal 0 beside a real total; a Subtotal row that does not add
  // up with the tax to the total would contradict the document, so it is left off.
  const subtotalReconciles = Math.abs(savedSubtotal + savedTax - savedTotal) < 0.01;
  const taxLabel = hasBreakdown
    ? taxLineLabel(
        invoiceTaxSummary(
          invoice,
          (Array.isArray(invoice?.line_tax_rates) ? invoice.line_tax_rates : []).map((rate) => ({ tax_rate: rate })),
          { tax_rate: invoice?.tax_rate, tax_exempt: invoice?.tax_exempt }
        ),
        formatCurrency
      )
    : null;

  // A voided or cancelled invoice is not "paid": voiding zeroes its totals, which read as paid
  // here (operator ruling 2026-10-24, flow 02 question 1). Say so, and offer no payment.
  if (isInvoiceVoided(invoice?.status)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center" data-testid="pay-invoice-voided">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationCircleIcon className="w-8 h-8 text-gray-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Invoice Voided</h2>
          <p className="text-gray-600 mb-6">
            This invoice was voided{invoice?.company?.name ? ` by ${invoice.company.name}` : ''}. No payment is due.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Invoice #</span>
              <span className="font-medium">{invoice?.invoice_number}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (invoicePaid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Invoice Paid</h2>
          <p className="text-gray-600 mb-6">This invoice has already been paid.</p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Invoice #</span>
              <span className="font-medium">{invoice?.invoice_number}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-500">Balance Due</span>
              <span className="font-medium">{formatCurrency(0)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const brand = portalBrand(invoice?.company);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Back Button */}
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back
        </button>

        {/* Payment Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          {/* Brand colours (operator ruling 2026-10-30, item 8; utils/portalBrand.js): the header in
              the primary colour with contrast-checked text, a secondary rule under it. A company
              that never picked colours keeps the stock indigo header. */}
          <div
            className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white"
            style={brand.primary ? {
              backgroundImage: 'none',
              backgroundColor: brand.primary.accent,
              color: brand.primary.onAccent,
              ...(brand.rule ? { borderBottom: `4px solid ${brand.rule.accent}` } : {}),
            } : undefined}
            data-testid="pay-invoice-header"
          >
            <h1 className="text-xl font-bold mb-2">{canPayOnline ? 'Pay Invoice' : 'Invoice'}</h1>
            <p className={brand.primary ? undefined : 'text-indigo-100'}>{invoice?.company?.name}</p>
          </div>

          {/* Invoice Details */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm text-gray-500">Invoice Number</p>
                <p className="font-semibold text-gray-900">#{invoice?.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Amount Due</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(amountDue)}
                </p>
              </div>
            </div>

            {hasBreakdown && (
              <div className="mb-4 space-y-1 text-sm" data-testid="pay-invoice-breakdown">
                {subtotalReconciles && (
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(savedSubtotal)}</span>
                  </div>
                )}
                {taxLabel && (
                  <div className="flex justify-between text-gray-600" data-testid="pay-invoice-tax-row">
                    <span>{taxLabel}</span>
                    <span>{formatCurrency(savedTax)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium text-gray-900">
                  <span>Total</span>
                  <span>{formatCurrency(savedTotal)}</span>
                </div>
                {amountPaid > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Paid</span>
                    <span>-{formatCurrency(amountPaid)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-100 pt-1">
                  <span>Amount due</span>
                  <span>{formatCurrency(amountDue)}</span>
                </div>
              </div>
            )}

            {invoice?.customer && (
              <div className="text-sm text-gray-600">
                <p><strong>Bill to:</strong> {invoice.customer.name}</p>
                {invoice.customer.email && <p>{invoice.customer.email}</p>}
              </div>
            )}

            {/* The job's service location when it is not the billing address (operator ruling
                2026-10-24 #4, board #6215; shared owner documentServiceLocation). */}
            {serviceLocation && (
              <div className="text-sm text-gray-600 mt-3" data-testid="pay-invoice-service-location">
                <p><strong>Service location:</strong></p>
                {serviceLocation.map((line) => <p key={line}>{line}</p>)}
              </div>
            )}
          </div>

          {/* Payment Section */}
          {!canPayOnline ? (
            <div className="p-6" data-testid="pay-invoice-offline">
              <HowToPay instructions={invoice?.payment_instructions} companyName={invoice?.company?.name} />
            </div>
          ) : (
          <div className="p-6">
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <LockClosedIcon className="w-5 h-5 text-green-600" />
                <span className="font-medium text-gray-900">Secure Payment</span>
              </div>
              <p className="text-sm text-gray-600">
                You will be redirected to Stripe's secure checkout page to complete your payment.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
                <ExclamationCircleIcon className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}

            <button
              onClick={handlePayNow}
              disabled={processing}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={brandButtonStyle(brand.cta)}
              data-testid="pay-invoice-button"
            >
              {processing ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <CreditCardIcon className="w-5 h-5" />
                  Pay {formatCurrency(amountDue)}
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-4">
              <LockClosedIcon className="w-4 h-4" />
              <span>Secured by Stripe</span>
            </div>
          </div>
          )}
        </div>

        {/* Security Notice */}
        {canPayOnline && (
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Powered by Stripe</p>
            <p className="mt-1">256-bit SSL encryption</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayInvoice;
